// mds-dojo — dijit/form/_SearchMixin — FAITHFUL port of references/dijit/form/_SearchMixin.js.
// Codemod pipeline; declare from the faithful runtime. Adaptations:
//  - unused `dojo/query` import dropped (the source imports it but never calls it; the local
//    `query` variable shadows it — genuinely dead).
import declare from '../../dojo/_base/declare-runtime'; // declare
import keys from '../../dojo/keys'; // keys
import lang from '../../dojo/_base/lang'; // lang.clone lang.hitch
import string from '../../dojo/string'; // string.substitute
import when from '../../dojo/when';
import registry from '../registry'; // registry.byId

export default declare("dijit.form._SearchMixin", null, {
	// summary:
	//		A mixin that implements the base functionality to search a store based upon user-entered text such as
	//		with `dijit/form/ComboBox` or `dijit/form/FilteringSelect`
	// tags:
	//		protected

	// pageSize: Integer
	//		Argument to data provider.
	//		Specifies maximum number of search results to return per query
	pageSize: Infinity,

	// store: [const] dojo/store/api/Store
	//		Reference to data provider object used by this ComboBox.
	//		The store must accept an object hash of properties for its query. See `query` and `queryExpr` for details.
	store: null,

	// fetchProperties: Object
	//		Mixin to the store's fetch.
	fetchProperties: {},

	// query: Object
	//		A query that can be passed to `store` to initially filter the items.
	query: {},

	// list: [const] String
	//		Alternate to specifying a store.  Id of a dijit/form/DataList widget.
	list: "",
	_setListAttr: function (list: any) {
		// Avoid having list applied to the DOM node, since it has native meaning in modern browsers
		this._set("list", list);
	},

	// searchDelay: Integer
	//		Delay in milliseconds between when user types something and we start
	//		searching based on that value
	searchDelay: 200,

	// searchAttr: String
	//		Search for items in the data store where this attribute (in the item)
	//		matches what the user typed
	searchAttr: "name",

	// queryExpr: String
	//		This specifies what query is sent to the data store,
	//		based on what the user has typed.
	//		`${0}` will be substituted for the user text.
	//		`*` is used for wildcards.
	//		`${0}*` means "starts with", `*${0}*` means "contains", `${0}` means "is"
	queryExpr: "${0}*",

	// ignoreCase: Boolean
	//		Set true if the query should ignore case when matching possible items
	ignoreCase: true,

	_patternToRegExp: function (pattern: any) {
		// summary:
		//		Helper function to convert a simple pattern to a regular expression for matching.
		return new RegExp("^" + pattern.replace(/(\\.)|(\*)|(\?)|\W/g, function (str: any, literal: any, star: any, question: any) {
			return star ? ".*" : question ? "." : literal ? literal : "\\" + str;
		}) + "$", this.ignoreCase ? "mi" : "m");
	},

	_abortQuery: function () {
		// stop in-progress query
		if (this.searchTimer) {
			this.searchTimer = this.searchTimer.remove();
		}
		if (this._queryDeferHandle) {
			this._queryDeferHandle = this._queryDeferHandle.remove();
		}
		if (this._fetchHandle) {
			if (this._fetchHandle.abort) {
				this._cancelingQuery = true;
				this._fetchHandle.abort();
				this._cancelingQuery = false;
			}
			if (this._fetchHandle.cancel) {
				this._cancelingQuery = true;
				this._fetchHandle.cancel();
				this._cancelingQuery = false;
			}
			this._fetchHandle = null;
		}
	},

	_processInput: function (/*Event*/ evt: any) {
		// summary:
		//		Handles input (keyboard/paste) events
		if (this.disabled || this.readOnly) { return; }
		var key = evt.charOrCode;

		this._prev_key_backspace = false;

		if (key === keys.DELETE || key === keys.BACKSPACE) {
			this._prev_key_backspace = true;
			this._maskValidSubsetError = true;
		}

		// need to wait a tad before start search so that the event
		// bubbles through DOM and we have value visible
		if (!this.store) {
			this.onSearch();
		} else {
			this.searchTimer = this.defer("_startSearchFromInput", 1);
		}
	},

	onSearch: function (/*===== results, query, options =====*/) {
		// summary:
		//		Callback when a search completes.
		// tags:
		//		callback
	},

	_startSearchFromInput: function () {
		this._startSearch(this.focusNode.value);
	},

	_startSearch: function (/*String*/ text: any) {
		// summary:
		//		Starts a search for elements matching text (text=="" means to return all items),
		//		and calls onSearch(...) when the search completes, to display the results.

		this._abortQuery();
		var
			_this = this,
			// Setup parameters to be passed to store.query().
			query = lang.clone(this.query), // #5970
			options: any = {
				start: 0,
				count: this.pageSize,
				queryOptions: {		// remove for 2.0
					ignoreCase: this.ignoreCase,
					deep: true
				}
			},
			qs = string.substitute(this.queryExpr, [text.replace(/([\\\*\?])/g, "\\$1")]),
			q: any,
			startQuery = function () {
				var resPromise = _this._fetchHandle = _this.store.query(query, options);
				if (_this.disabled || _this.readOnly || (q !== _this._lastQuery)) {
					return;
				} // avoid getting unwanted notify
				when(resPromise, function (res: any) {
					_this._fetchHandle = null;
					if (!_this.disabled && !_this.readOnly && (q === _this._lastQuery)) { // avoid getting unwanted notify
						when(resPromise.total, function (total: any) {
							res.total = total;
							var pageSize = _this.pageSize;
							if (isNaN(pageSize) || pageSize > res.total) { pageSize = res.total; }
							// Setup method to fetching the next page of results
							res.nextPage = function (direction: any) {
								//	tell callback the direction of the paging so the screen
								//	reader knows which menu option to shout
								options.direction = direction = direction !== false;
								options.count = pageSize;
								if (direction) {
									options.start += res.length;
									if (options.start >= res.total) {
										options.count = 0;
									}
								} else {
									options.start -= pageSize;
									if (options.start < 0) {
										options.count = Math.max(pageSize + options.start, 0);
										options.start = 0;
									}
								}
								if (options.count <= 0) {
									res.length = 0;
									_this.onSearch(res, query, options);
								} else {
									startQuery();
								}
							};
							_this.onSearch(res, query, options);
						});
					}
				}, function (err: any) {
					_this._fetchHandle = null;
					if (!_this._cancelingQuery) {	// don't treat canceled query as an error
						console.error(_this.declaredClass + ' ' + err.toString());
					}
				});
			};

		lang.mixin(options, this.fetchProperties);

		// Generate query
		if (this.store._oldAPI) {
			// remove this branch for 2.0
			q = qs;
		} else {
			// Query on searchAttr is a regex for benefit of dojo/store/Memory,
			// but with a toString() method to help dojo/store/JsonRest.
			// Search string like "Co*" converted to regex like /^Co.*$/i.
			q = this._patternToRegExp(qs);
			q.toString = function () { return qs; };
		}

		// set _lastQuery, *then* start the timeout
		// otherwise, if the user types and the last query returns before the timeout,
		// _lastQuery won't be set and their input gets rewritten
		this._lastQuery = query[this.searchAttr] = q;
		this._queryDeferHandle = this.defer(startQuery, this.searchDelay);
	},

	//////////// INITIALIZATION METHODS ///////////////////////////////////////

	constructor: function () {
		this.query = {};
		this.fetchProperties = {};
	},

	postMixInProperties: function () {
		if (!this.store) {
			var list = this.list;
			if (list) {
				this.store = registry.byId(list);
			}
		}
		this.inherited(arguments);
	}
});
